import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { Trash2, FileText, Shield, KeyRound, Bell, Send } from "lucide-react";
import { RestartOnboardingButton } from "../components/Onboarding";

export default function Settings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");

  const load = () => api.get("/resumes").then((r) => setResumes(r.data || []));
  useEffect(() => { load(); }, []);

  const removeOne = async (id) => {
    try {
      await api.delete(`/resumes/${id}`);
      toast.success("Resume deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (!cur || !next) return toast.error("Fill both fields");
    try {
      await api.post("/settings/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteAcct = async () => {
    if (!window.confirm("This permanently deletes your account and all your data. Continue?")) return;
    try {
      await api.delete("/user/delete");
      await logout();
      toast.success("Account deleted");
      nav("/");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">Settings</p>
        <h1 className="font-display font-black text-4xl tracking-tight mt-1">Your account</h1>
      </div>

      <Section title="Profile" icon={Shield}>
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <Info label="Name" value={user?.name} />
          <Info label="Email" value={user?.email} />
          <Info label="Sign-in" value={user?.auth_provider === "google" ? "Google" : "Email & password"} />
          <Info label="Subscription" value={user?.subscription === "premium" ? "Premium" : "Free"} />
        </div>
      </Section>

      <MatchAlertsCard isPremium={user?.subscription === "premium" || user?.is_admin} />

      <Section title="Welcome tour" icon={Shield}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-bold text-sm text-white">Replay the 3-step welcome tour</div>
            <div className="text-xs text-slate-500 mt-0.5">See the Career Profile → Job Agent → Pipeline promise again.</div>
          </div>
          <RestartOnboardingButton />
        </div>
      </Section>

      {user?.auth_provider === "password" && (
        <Section title="Change password" icon={KeyRound}>
          <form onSubmit={changePw} className="grid sm:grid-cols-2 gap-4">
            <label>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Current password</span>
              <input data-testid="cur-pw" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5 w-full rounded-xl border-2 border-slate-200 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm" />
            </label>
            <label>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">New password</span>
              <input data-testid="new-pw" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1.5 w-full rounded-xl border-2 border-slate-200 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm" />
            </label>
            <div className="sm:col-span-2">
              <button data-testid="pw-submit" className="rounded-xl bg-[#1E6FE6] hover:bg-[#1257C4] text-white px-5 py-2.5 text-sm font-bold">Update password</button>
            </div>
          </form>
        </Section>
      )}

      <Section title="Uploaded resumes" icon={FileText}>
        {resumes.length === 0 && <div className="text-sm text-slate-500">No resumes uploaded yet.</div>}
        <ul className="space-y-2">
          {resumes.map((r) => (
            <li key={r.resume_id} className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{r.filename}</div>
                <div className="text-xs text-slate-500 truncate">{r.parsed?.current_title || "—"}</div>
              </div>
              <button
                data-testid={`delete-resume-${r.resume_id}`}
                onClick={() => removeOne(r.resume_id)}
                className="grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Danger zone" icon={Trash2}>
        <p className="text-sm text-slate-500 mb-3">Permanently delete your account and all related data. This cannot be undone.</p>
        <button
          data-testid="delete-account"
          onClick={deleteAcct}
          className="inline-flex items-center gap-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 px-5 py-2.5 text-sm font-bold border border-red-200"
        >
          <Trash2 size={14} /> Delete my account
        </button>
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-100 p-6 fade-up-delay-1">
      <div className="flex items-center gap-2 mb-4">
        <span className="grid place-items-center w-8 h-8 rounded-xl bg-[#E6F0FF] text-[#1E6FE6]">
          <Icon size={16} />
        </span>
        <h2 className="font-display font-bold text-lg">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-800 font-medium mt-0.5 break-all">{value || "—"}</div>
    </div>
  );
}


const DAYS = [
  { v: "mon", l: "Monday" }, { v: "tue", l: "Tuesday" }, { v: "wed", l: "Wednesday" },
  { v: "thu", l: "Thursday" }, { v: "fri", l: "Friday" }, { v: "sat", l: "Saturday" }, { v: "sun", l: "Sunday" },
];

function MatchAlertsCard({ isPremium }) {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/notifications/prefs").then((r) => setPrefs(r.data?.prefs)).catch(() => setPrefs(null));
  }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const body = {
        enabled: patch.enabled ?? prefs?.match_alerts_enabled ?? true,
        day: patch.day ?? prefs?.day ?? "mon",
        hour_utc: patch.hour_utc ?? prefs?.hour_utc ?? 9,
      };
      const r = await api.patch("/notifications/prefs", body);
      setPrefs(r.data?.prefs);
      toast.success("Alert preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const sendPreview = async () => {
    if (!isPremium) { toast.error("Premium only. Upgrade to send yourself a preview."); return; }
    setSending(true);
    try {
      const r = await api.post("/notifications/send-preview");
      toast.success(`Preview sent — ${r.data?.new_count || 0} new + ${r.data?.best_count || 0} best-fit roles`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send preview");
    } finally { setSending(false); }
  };

  if (!prefs) {
    return (
      <Section title="Match Alerts" icon={Bell}>
        <div className="text-sm text-slate-500">Loading preferences…</div>
      </Section>
    );
  }

  const enabled = !!prefs.match_alerts_enabled;
  const last = prefs.last_sent_at ? new Date(prefs.last_sent_at).toLocaleString() : null;
  const localHourLabel = (h) => {
    try {
      const d = new Date(Date.UTC(2026, 0, 5, h, 0, 0));
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch { return `${h}:00 UTC`; }
  };

  return (
    <Section title="Match Alerts" icon={Bell}>
      <div className="space-y-4" data-testid="match-alerts-card">
        <p className="text-sm text-slate-500 leading-relaxed">
          A weekly email digest with your top verified job matches — 3 new + 2 best-fit ongoing.
          Delivered at the day &amp; hour of your choice.
        </p>

        <div className="flex items-center justify-between rounded-2xl border border-slate-100 p-4 bg-slate-50/50">
          <div>
            <div className="font-bold text-sm">Weekly digest</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {enabled ? "You'll get the digest at your scheduled time each week." : "Alerts are off. Turn on to receive weekly matches."}
            </div>
            {last && (<div className="text-[11px] text-slate-400 mt-1">Last sent: {last}</div>)}
          </div>
          <button
            data-testid="alerts-toggle"
            role="switch"
            aria-checked={enabled}
            disabled={busy}
            onClick={() => save({ enabled: !enabled })}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-60 ${enabled ? "bg-[var(--cavilo-blue)]" : "bg-slate-300"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Day of week (UTC)</span>
            <select
              data-testid="alerts-day"
              value={prefs.day}
              disabled={!enabled || busy}
              onChange={(e) => save({ day: e.target.value })}
              className="mt-1.5 w-full rounded-xl border-2 border-slate-200 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm bg-white disabled:opacity-60"
            >
              {DAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Hour (UTC)</span>
            <select
              data-testid="alerts-hour"
              value={prefs.hour_utc}
              disabled={!enabled || busy}
              onChange={(e) => save({ hour_utc: Number(e.target.value) })}
              className="mt-1.5 w-full rounded-xl border-2 border-slate-200 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm bg-white disabled:opacity-60"
            >
              {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC · {localHourLabel(h)} local</option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
          <div className="text-xs text-slate-500">
            {isPremium ? "Send a copy to your inbox to see how it looks." : "Premium required to receive Match Alerts."}
          </div>
          <button
            data-testid="alerts-preview"
            onClick={sendPreview}
            disabled={sending || !isPremium}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--cavilo-ink)] hover:bg-[#1a2237] text-white px-5 py-2.5 text-sm font-bold disabled:opacity-60"
          >
            <Send size={14} /> {sending ? "Sending…" : "Send me a preview"}
          </button>
        </div>
      </div>
    </Section>
  );
}
